import React, { createContext, useState, useCallback, useEffect, useMemo, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import { supabase } from '../config/supabase';

// Create the context
const AuthContext = createContext(null);

// Helper function for organization data
const getUserOrganizationData = async (userId) => {
  try {
    const { data: memberData, error: memberError } = await supabase
      .from('organization_members')
      .select(`
        role,
        organization_id,
        organization:organizations (
          id,
          name,
          slug,
          settings,
          email_provider_configured
        )
      `)
      .eq('user_id', userId)
      .single();

    if (memberError) {
      console.error('Error fetching member data:', memberError);
      return null;
    }

    const { data: deptData, error: deptError } = await supabase
      .from('department_members')
      .select(`
        role,
        department:departments (
          id,
          name,
          description
        )
      `)
      .eq('user_id', userId)
      .maybeSingle();

    return {
      role: memberData.role,
      organization: memberData.organization,
      organization_id: memberData.organization_id,
      department: deptError ? null : deptData?.department || null,
      department_role: deptError ? null : deptData?.role || null,
    };
  } catch (error) {
    console.error('Error in getUserOrganizationData:', error);
    return null;
  }
};

// AuthProvider component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if current route is public - FIXED VERSION FOR VERIFY ROUTES
const isPublicRoute = useMemo(() => {
  // Special case for verification routes - ALWAYS return true for these
  if (location.pathname.startsWith('/verify/')) {
    console.log('Verification route detected, marking as public:', location.pathname);
    return true;
  }
  
  // Regular public paths
  const publicPaths = [
    '/login',
    '/signup',
    '/reset-password',
    '/auth/callback'
  ];
  
  const isPublic = publicPaths.some(path => location.pathname.startsWith(path));
  console.log(`Route ${location.pathname} public status: ${isPublic}`);
  return isPublic;
}, [location.pathname]);

  // Update user profile
  const updateUserProfile = useCallback(async (userData) => {
    setUser(prev => ({
      ...prev,
      ...userData
    }));
  }, []);

  // Set user with organization data
  const setUserWithOrganization = useCallback(async (userData) => {
    if (!userData?.id) {
      console.log('No user ID, setting user to null');
      setUser(null);
      return null;
    }
    
    try {
      console.log('Fetching organization data for user:', userData.id);
      const orgData = await getUserOrganizationData(userData.id);
      
      const enhancedUser = {
        ...userData,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phoneNumber: userData.phoneNumber || '',
        jobTitle: userData.jobTitle || '',
        role: orgData?.role || 'member',
        organization: orgData?.organization || null,
        organization_id: orgData?.organization_id,
        department: orgData?.department || null,
        department_role: orgData?.department_role || null,
        onboardingComplete: !!orgData?.organization_id, // Add onboardingComplete flag
        permissions: {
          isAdmin: orgData?.role === 'admin',
          isDomainAdmin: orgData?.role === 'domain_admin',
          canViewOrgData: ['admin', 'domain_admin'].includes(orgData?.role),
          canManageUsers: ['admin'].includes(orgData?.role),
          canManageSettings: ['admin'].includes(orgData?.role),
          canViewAnalytics: ['admin', 'domain_admin'].includes(orgData?.role),
        }
      };

      console.log('Setting enhanced user:', enhancedUser.email);
      setUser(enhancedUser);
      return enhancedUser;
    } catch (error) {
      console.error('Error in setUserWithOrganization:', error);
      const basicUser = {
        ...userData,
        role: 'member',
        onboardingComplete: false,
        permissions: {
          isAdmin: false,
          isDomainAdmin: false,
          canViewOrgData: false,
          canManageUsers: false,
          canManageSettings: false,
          canViewAnalytics: false,
        }
      };
      setUser(basicUser);
      return basicUser;
    }
  }, []);

  // Login function
  const login = useCallback(async (token) => {
    try {
      console.log('Login started with token');
      localStorage.setItem('token', token);
      
      await supabase.auth.setSession({
        access_token: token,
        refresh_token: token
      });

      console.log('Getting current user data');
      const response = await authAPI.getCurrentUser();
      console.log('User data received, setting user');
      
      const enhancedUser = await setUserWithOrganization(response.data);
      console.log('User set, navigating to dashboard');
      
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      localStorage.removeItem('token');
      throw error;
    }
  }, [navigate, setUserWithOrganization]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      console.log('Logging out');
      await supabase.auth.signOut();
      localStorage.removeItem('token');
      setUser(null);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }, [navigate]);

  // Google login functions
  const initiateGoogleLogin = useCallback(() => {
    console.log('Initiating Google login');
    localStorage.setItem('signupFlow', 'false');
    window.location.href = `${authAPI.googleLogin()}?flowType=login`;
  }, []);

  const initiateGoogleSignup = useCallback(() => {
    console.log('Initiating Google signup');
    localStorage.setItem('signupFlow', 'true');
    window.location.href = `${authAPI.googleLogin()}?flowType=signup`;
  }, []);

  // Create a default free subscription for new users
  const createDefaultSubscription = useCallback(async (userData) => {
    try {
      console.log('Creating default subscription for new user');
      const response = await authAPI.createDefaultSubscription();
      console.log('Default subscription created:', response);
      return true;
    } catch (error) {
      console.error('Failed to create default subscription:', error);
      return false;
    }
  }, []);

  // Handle authentication on mount and URL changes
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Skip OAuth processing if we're on a verification page
      if (location.pathname.startsWith('/verify/')) {
        console.log('Verification route detected, skipping OAuth processing');
        setIsLoading(false);
        return;
      }
      
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      
      console.log('OAuth callback check - token present:', !!token);
      
      if (!token) {
        setIsLoading(false);
        return;
      }
  
      try {
        console.log('Processing OAuth callback with token');
        localStorage.setItem('token', token);
        
        await supabase.auth.setSession({
          access_token: token,
          refresh_token: token
        });
  
        console.log('Getting user data after OAuth callback');
        const response = await authAPI.getCurrentUser();
        const enhancedUser = await setUserWithOrganization(response.data);
        
        // Check if this was a signup flow
        const isSignupFlow = localStorage.getItem('signupFlow') === 'true';
        console.log('Is signup flow:', isSignupFlow);
        
        // Clear signup flow marker
        localStorage.removeItem('signupFlow');
        
        // If this is a new user without an organization, create a default subscription
        if (!enhancedUser.organization_id) {
          console.log('New user detected, creating default subscription');
          await createDefaultSubscription(enhancedUser);
          
          // Refresh user data to get the new organization
          const updatedResponse = await authAPI.getCurrentUser();
          await setUserWithOrganization(updatedResponse.data);
        }
        
        // Always navigate to dashboard after authentication
        console.log('Authentication complete, navigating to dashboard');
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('OAuth callback error:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('signupFlow');
        navigate('/login', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };
  
    const checkExistingAuth = async () => {
      // Skip auth check for public routes and verification routes
      if (isPublicRoute || location.pathname.startsWith('/verify/')) {
        console.log('Public or verification route detected, skipping auth check:', location.pathname);
        setIsLoading(false);
        return;
      }
  
      const token = localStorage.getItem('token');
      console.log('Checking existing auth - token present:', !!token);
      
      if (!token) {
        setIsLoading(false);
        console.log('No token found, redirecting to login');
        navigate('/login', { replace: true });
        return;
      }
  
      try {
        console.log('Validating existing token');
        const response = await authAPI.getCurrentUser();
        console.log('Token valid, setting user');
        await setUserWithOrganization(response.data);
      } catch (error) {
        console.error('Token validation error:', error);
        localStorage.removeItem('token');
        navigate('/login', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };
  
    // Special case for verification routes - bypass all auth checks
    if (location.pathname.startsWith('/verify/')) {
      console.log('Verification route detected, bypassing all auth processing');
      setIsLoading(false);
      return;
    }
  
    // Check if we're handling an OAuth callback
    if (location.search.includes('token=') && !location.pathname.startsWith('/verify/')) {
      console.log('OAuth callback URL detected');
      handleOAuthCallback();
    } else {
      checkExistingAuth();
    }
  }, [location, navigate, setUserWithOrganization, isPublicRoute, createDefaultSubscription]);

    

  // Create context value
  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    isPublicRoute,
    login,
    logout,
    updateUserProfile,
    permissions: user?.permissions || {},
    organization: user?.organization || null,
    department: user?.department || null,
    organizationId: user?.organization_id || null,
    onboardingComplete: user?.onboardingComplete || false,
    initiateGoogleLogin,
    initiateGoogleSignup
  }), [
    user, 
    isLoading, 
    isPublicRoute, 
    login, 
    logout, 
    updateUserProfile, 
    initiateGoogleLogin, 
    initiateGoogleSignup
  ]);

  // Add loading state handling that respects public routes
  if (isLoading && !isPublicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook for using the auth context
function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined || context === null) {
    console.error('AuthContext is undefined. Make sure you are using AuthProvider correctly');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  // Debug log
  console.debug('Auth Context State:', {
    isAuthenticated: context.isAuthenticated,
    hasUser: !!context.user,
    hasOrgId: !!context.organizationId,
    organizationId: context.organizationId,
    isPublicRoute: context.isPublicRoute,
    onboardingComplete: context.onboardingComplete
  });
  
  return context;
}

export { AuthProvider, useAuth, AuthContext };